/**
 * Date utilities for portal package - specifically for offerte date selection
 * Handles planning date restrictions and validation for DatePicker components
 */

/**
 * Converts any date input to a Date object
 * @param date - Date object, ISO string, or any date-like input
 * @returns Date object or null if invalid
 */
export function toDateObject(date: Date | string | null | undefined): Date | null {
  if (!date) return null;

  try {
    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date provided to toDateObject:', date);
      return null;
    }

    return dateObj;
  } catch (error) {
    console.warn('Error converting to Date object:', date, error);
    return null;
  }
}

/**
 * Formats a date for display in Dutch locale
 * @param date - Date object, ISO string, or any date-like input
 * @param includeTime - Whether to include time in the format
 * @returns Formatted date string or "Niet ingesteld" if invalid
 */
export function formatDateForDisplay(
  date: Date | string | null | undefined,
  includeTime: boolean = false
): string {
  const dateObj = toDateObject(date);

  if (!dateObj) return "Niet ingesteld";

  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };

  if (includeTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
  }

  return dateObj.toLocaleDateString("nl-NL", options);
}

/**
 * Converts planning timestamp strings to Date objects for DatePicker
 * @param timestamps - Array of timestamp strings (planning_events beschikbaar days)
 * @returns Array of Date objects, filtered for valid dates only
 */
export function convertPlanningTimestampsTodates(timestamps: string[] | null | undefined): Date[] {
  if (!timestamps || !Array.isArray(timestamps)) {
    return [];
  }

  return timestamps
    .map(timestamp => toDateObject(timestamp))
    .filter((date): date is Date => date !== null);
}

/**
 * Checks if a date is available in the planning timestamps
 * @param date - Date to check
 * @param availableDates - Array of available Date objects
 * @returns True if the date is available, false otherwise
 */
export function isDateAvailable(date: Date | null, availableDates: Date[]): boolean {
  if (!date || !availableDates.length) {
    return false;
  }

  return availableDates.some(availableDate =>
    isSameDay(date, availableDate)
  );
}

/**
 * Checks if two dates are the same day (ignoring time)
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if same day, false otherwise
 */
export function isSameDay(
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined
): boolean {
  const d1 = toDateObject(date1);
  const d2 = toDateObject(date2);

  if (!d1 || !d2) return false;

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Creates a function to disable dates in PrimeVue DatePicker
 * @param availableDates - Array of available Date objects
 * @returns Function that returns true if date should be disabled
 */
export function createDateDisableFunction(availableDates: Date[]): (date: Date) => boolean {
  return (date: Date) => {
    // Disable if date is not in available dates
    return !isDateAvailable(date, availableDates);
  };
}

/**
 * Validates that all required inspection dates are selected
 * @param selectedDates - Object with inspection types as keys and selected dates as values
 * @param requiredInspectionTypes - Array of inspection types that require dates
 * @returns Object with validation result and missing types
 */
export function validateInspectionDates(
  selectedDates: Record<string, Date | null>,
  requiredInspectionTypes: string[]
): { isValid: boolean; missingTypes: string[] } {
  const missingTypes = requiredInspectionTypes.filter(
    type => !selectedDates[type]
  );

  return {
    isValid: missingTypes.length === 0,
    missingTypes
  };
}

/**
 * Gets the minimum date for DatePicker (today)
 * @returns Today's date at midnight
 */
export function getMinSelectableDate(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Gets the maximum date for DatePicker (1 year from now)
 * @returns Date 1 year from today
 */
export function getMaxSelectableDate(): Date {
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  return maxDate;
}

/**
 * Converts selected dates to a format suitable for storage
 * @param selectedDates - Object with inspection types as keys and selected dates as values
 * @returns Object with inspection types as keys and ISO strings as values
 */
export function convertSelectedDatesToStorage(
  selectedDates: Record<string, Date | null>
): Record<string, string | null> {
  const result: Record<string, string | null> = {};

  Object.entries(selectedDates).forEach(([type, date]) => {
    result[type] = date ? date.toISOString() : null;
  });

  return result;
}
